

import React, { lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { getAllEraRoutes } from "@/config/era-registry";

import { useScrollToTop } from "./hooks/useScrollToTop";
import Header from "./components/ink-insanity/Header";

// Eagerly load the landing page
import Home from "./pages/Home";
import Index from "./pages/Index";
import CharacterFlowLayout from "./components/ink-insanity/CharacterFlowLayout";

// Lazy load all other routes
const Characteristics = lazy(() => import("./pages/Characteristics"));
const NotFound = lazy(() => import("./pages/NotFound"));
const OccupationSkills = lazy(() => import("./pages/OccupationSkills"));
const PersonalSkills = lazy(() => import("./pages/PersonalSkills"));
const CharacterBackstory = lazy(() => import("./pages/CharacterBackstory"));
const Equipment = lazy(() => import("./pages/Equipment"));
const Dossier = lazy(() => import("./pages/Dossier"));
const CharacterSheet = lazy(() => import("./pages/CharacterSheet"));
const Changelog = lazy(() => import("./pages/Changelog"));
const Blog = lazy(() => import("./pages/Blog"));
const About = lazy(() => import("./pages/About"));
const DataExplorer = lazy(() => import("./pages/DataExplorer"));
const SharedCharacter = lazy(() => import("./pages/SharedCharacter"));
const ShortShare = lazy(() => import("./pages/ShortShare"));
const Party = lazy(() => import("./pages/Party"));
const GaslightIndex = lazy(() => import("./pages/GaslightIndex"));
const ModernIndex = lazy(() => import("./pages/ModernIndex"));
const RegencyIndex = lazy(() => import("./pages/RegencyIndex"));
const WesternIndex = lazy(() => import("./pages/WesternIndex"));
const DarkAgesIndex = lazy(() => import("./pages/DarkAgesIndex"));
const InvictusIndex = lazy(() => import("./pages/InvictusIndex"));
const Pulp1930sIndex = lazy(() => import("./pages/Pulp1930sIndex"));
const Archetype = lazy(() => import("./pages/Archetype"));
const Talents = lazy(() => import("./pages/Talents"));
const DesignSystem = lazy(() => import("./pages/DesignSystem"));
const Dice = lazy(() => import("./pages/Dice"));
const FeedbackAdmin = lazy(() => import("./pages/FeedbackAdmin"));

const queryClient = new QueryClient();

const AppContent: React.FC = () => {
  useScrollToTop();
  
  const location = useLocation();
  const isHome = location.pathname === "/";
  const eraRoutes = getAllEraRoutes();
  const flowRoutes = [...eraRoutes, '/archetype', '/talents', '/characteristics', '/occupation-skills', '/personal-skills', '/backstory', '/equipment', '/dossier'];
  const isFlowRoute = flowRoutes.includes(location.pathname);

  return (
    <>
      <Toaster />
      <Sonner position="top-center" closeButton richColors />
      {!isHome && !isFlowRoute && <Header />}
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Home />} />
          {/* Character creation flow — persistent layout shell */}
          <Route element={<CharacterFlowLayout />}>
            <Route path="/1920s" element={<Index />} />
            <Route path="/gaslight" element={<GaslightIndex />} />
            <Route path="/modern" element={<ModernIndex />} />
            <Route path="/regency" element={<RegencyIndex />} />
            <Route path="/western" element={<WesternIndex />} />
            <Route path="/dark-ages" element={<DarkAgesIndex />} />
            <Route path="/invictus" element={<InvictusIndex />} />
            <Route path="/pulp-1930s" element={<Pulp1930sIndex />} />
            <Route path="/archetype" element={<Archetype />} />
            <Route path="/talents" element={<Talents />} />
            <Route path="/characteristics" element={<Characteristics />} />
            <Route path="/occupation-skills" element={<OccupationSkills />} />
            <Route path="/personal-skills" element={<PersonalSkills />} />
            <Route path="/backstory" element={<CharacterBackstory />} />
            <Route path="/equipment" element={<Equipment />} />
            <Route path="/dossier" element={<Dossier />} />
          </Route>
          <Route path="/character-sheet" element={<CharacterSheet />} />
          <Route path="/data-explorer" element={<DataExplorer />} />
          <Route path="/shared" element={<SharedCharacter />} />
          <Route path="/s/:code" element={<ShortShare />} />
          <Route path="/party" element={<Party />} />
          <Route path="/changelog" element={<Changelog />} />
          <Route path="/changelog/:slug" element={<Blog />} />
          <Route path="/about" element={<About />} />
          <Route path="/design-system" element={<DesignSystem />} />
          <Route path="/dice" element={<Dice />} />
          <Route path="/feedback-admin" element={<FeedbackAdmin />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

const App: React.FC = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
